import { reactive, computed } from 'vue'

const storageTokenKey = 'master_token'
const storageUserKey = 'master_session'
const apiBase = process.env.VUE_APP_API_BASE || 'http://localhost:3001'

// SEGURANÇA: a sessão do master (maior privilégio do sistema) fica em
// sessionStorage — não persiste em disco entre sessões do navegador, reduzindo
// a janela de uso de um token roubado. Migração de tokens antigos em
// localStorage é limpa no load.
export interface MasterAdmin {
  username: string
  name: string
}

function loadMasterSession(): MasterAdmin | null {
  try {
    // Limpa resquícios da versão que usava localStorage
    window.localStorage.removeItem(storageUserKey)
    window.localStorage.removeItem(storageTokenKey)
    const raw = window.sessionStorage.getItem(storageUserKey)
    if (!raw) return null
    return JSON.parse(raw) as MasterAdmin
  } catch {
    return null
  }
}

const state = reactive({
  admin: loadMasterSession() as MasterAdmin | null
})

export const masterSessionStore = {
  state,
  isLoggedIn: computed(() => !!state.admin),

  getToken(): string {
    try { return window.sessionStorage.getItem(storageTokenKey) || '' } catch { return '' }
  },

  login(admin: MasterAdmin, token: string) {
    state.admin = admin
    try {
      window.sessionStorage.setItem(storageUserKey, JSON.stringify(admin))
      window.sessionStorage.setItem(storageTokenKey, token)
    } catch { /* ignore */ }
  },

  logout() {
    state.admin = null
    try {
      window.sessionStorage.removeItem(storageUserKey)
      window.sessionStorage.removeItem(storageTokenKey)
    } catch { /* ignore */ }
  }
}

// Entra como admin de uma obra (impersonação de suporte). Mantém a sessão
// master intacta (sessionStorage separado) — para voltar, ir em /administrador.
export async function impersonateSystem(systemId: string): Promise<void> {
  const data = await masterFetch<{ account: unknown; token: string; impersonatedBy: string; siteName: string }>(
    `/master/systems/${encodeURIComponent(systemId)}/impersonate`,
    { method: 'POST', body: JSON.stringify({}) }
  )
  const { setAuthToken } = await import('./api')
  const { sessionStore } = await import('./session')
  setAuthToken(data.token, false)
  sessionStore.login(data.account as never, false)
  try { window.sessionStorage.setItem('imp_by', data.impersonatedBy || 'Administrador geral') } catch { /* ignore */ }
  window.location.href = '/'
}

export async function masterFetch<T>(path: string, options?: RequestInit): Promise<T> {
  const token = masterSessionStore.getToken()
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(options?.headers as Record<string, string> | undefined)
  }
  if (token) headers.Authorization = `Bearer ${token}`
  const response = await fetch(`${apiBase}${path}`, { ...options, headers, cache: 'no-store' })
  if (!response.ok) {
    // Token expirado/inválido (7 dias): encerra a sessão e volta ao login,
    // em vez de deixar o painel num estado "logado" com tudo falhando.
    if (response.status === 401 || response.status === 403) {
      masterSessionStore.logout()
      if (typeof window !== 'undefined' && !window.location.pathname.match(/^\/administrador\/?$/)) {
        window.location.replace('/administrador')
      }
      throw new Error('Sessão do administrador expirada. Faça login novamente.')
    }
    const payload = await response.json().catch(() => ({}))
    throw new Error(payload.error || 'Erro no servidor')
  }
  return (await response.json()) as T
}
